import React, { useState, useEffect, useMemo } from 'react';
import { logger } from '@/lib/logger';
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SideSheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Search,
  RefreshCw,
  AlertCircle,
  Loader2,
  ChevronRight,
  ChevronDown,
  Box,
  Check,
  MapPin,
  X,
  Split,
  Settings2,
  GripVertical,
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { useOrganization } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { categoryTemplateService, CategoryTemplate } from '@/services/categoryTemplateService';
import { PortalImportModal } from './PortalImportModal';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { portalUnlinkService } from '@/services/portalio/unlinkService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { SplitConfigModal } from './SplitConfigModal';
import { createSplitConfigService, type SplitItem } from '@/services/portalio/splitConfigService';

interface ImportedProduct {
  id: string;
  portal_product_id: string;
  reference_id?: string;
  portal_name: string;
  portal_description: string | null;
  portal_price: number;
  category: string;
  subcategory?: string;
  manufacturer: string | null;
  model: string | null;
  proposal_name: string | null;
  proposal_id: string | null;
  imported_at: string;
  product_library_id?: string;
  product_library?: {
    subcategory?: string;
  };
  portal_hierarchy: {
    area?: string;
    option?: string;
    item?: string;
  };
  portal_data?: {
    quantity?: number;
    [key: string]: any;
  };
  options: any;
}

interface HierarchicalStructure {
  [area: string]: {
    [option: string]: ImportedProduct[];
  };
}

export function PortalProductsDrawer() {
  const { organization } = useOrganization();
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const selectEntity = useAppStore(state => state.selectEntity);
  const isOpen = activeDrawer === 'portalProducts';

  // Access floor plan data for tracking placed products
  const { data: floorPlanData } = useFloorPlanContext();
  const { lvProducts = [], controls = [], fixtures = [] } = floorPlanData || {};

  const [products, setProducts] = useState<ImportedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentProposal, setCurrentProposal] = useState<{
    id: string;
    name: string;
    number?: string;
  } | null>(null);
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [expandedOptions, setExpandedOptions] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [placedProductIds, setPlacedProductIds] = useState<string[]>([]);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitCategories, setSplitCategories] = useState<Map<string, string>>(new Map());
  const [productToSplit, setProductToSplit] = useState<ImportedProduct | null>(null);
  const [splitConfigs, setSplitConfigs] = useState<Map<string, SplitItem[]>>(new Map());

  // Fetch category templates
  const { data: categoryTemplates = [] } = useQuery<CategoryTemplate[]>({
    queryKey: ['category_templates', 'lv_products'],
    queryFn: () => categoryTemplateService.getByDomain('lv_products'),
  });

  useEffect(() => {
    if (isOpen && organization?.id && floorPlanData?.floorPlan?.id) {
      loadProducts();
    }
  }, [isOpen, organization?.id, floorPlanData?.floorPlan?.id]);

  // Clear search query when opening the drawer
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Force re-render when lvProducts changes (real-time sync)
  useEffect(() => {
    // The placedProductsMap will automatically recalculate due to its dependencies
    // This effect ensures the component re-renders when products are added/removed from floor plan
    if (isOpen) {
      // Optionally show a subtle notification when products are placed/removed
      // (This is implicit through the visual updates)
    }
  }, [lvProducts, isOpen]);

  // Track which products are placed on the floor plan
  const placedProductsMap = useMemo(() => {
    const map = new Map<string, { count: number; rooms: string[] }>();

    lvProducts.forEach(lvProduct => {
      // Check if this is a Portal product by looking at metadata
      if (lvProduct.metadata?.from_portal && lvProduct.metadata?.portal_product_id) {
        const portalProductId = lvProduct.metadata.portal_product_id;

        // Handle split products differently
        if (
          lvProduct.metadata.is_split &&
          lvProduct.metadata.split_parent_id &&
          lvProduct.metadata.split_suffix
        ) {
          // Create a unique key for split items: parent_id + suffix (unique per instance)
          const splitKey = `${lvProduct.metadata.split_parent_id}_split_${lvProduct.metadata.split_suffix}`;
          const existing = map.get(splitKey) || { count: 0, rooms: [] };
          existing.count = 1; // Split items are unique, count is always 1 when placed

          // Track room placement
          if (lvProduct.room_id && floorPlanData?.rooms) {
            const room = floorPlanData.rooms.find(r => r.id === lvProduct.room_id);
            if (room && room.name && !existing.rooms.includes(room.name)) {
              existing.rooms.push(room.name);
            }
          }

          map.set(splitKey, existing);
        } else {
          // Regular Portal product (non-split)
          const existing = map.get(portalProductId) || { count: 0, rooms: [] };
          existing.count += 1;

          // Track room placement
          if (lvProduct.room_id && floorPlanData?.rooms) {
            const room = floorPlanData.rooms.find(r => r.id === lvProduct.room_id);
            if (room && room.name && !existing.rooms.includes(room.name)) {
              existing.rooms.push(room.name);
            }
          }

          map.set(portalProductId, existing);
        }
      }
    });

    // Track controls placement
    controls.forEach(control => {
      // Check if this is a Portal control by looking at category_info
      if (control.category_info?.from_portal && control.category_info?.portal_product_id) {
        const portalProductId = control.category_info.portal_product_id;

        // Handle split controls differently
        if (
          control.category_info.is_split &&
          control.category_info.split_parent_id &&
          control.category_info.split_suffix
        ) {
          // Create a unique key for split items: parent_id + suffix
          const splitKey = `${control.category_info.split_parent_id}_split_${control.category_info.split_suffix}`;
          const existing = map.get(splitKey) || { count: 0, rooms: [] };
          existing.count = 1; // Split items are unique, count is always 1 when placed

          // Track room placement
          if (control.room_id && floorPlanData?.rooms) {
            const room = floorPlanData.rooms.find(r => r.id === control.room_id);
            if (room && room.name && !existing.rooms.includes(room.name)) {
              existing.rooms.push(room.name);
            }
          }

          map.set(splitKey, existing);
        } else {
          // Regular Portal control (non-split)
          const existing = map.get(portalProductId) || { count: 0, rooms: [] };
          existing.count += 1;

          // Track room placement
          if (control.room_id && floorPlanData?.rooms) {
            const room = floorPlanData.rooms.find(r => r.id === control.room_id);
            if (room && room.name && !existing.rooms.includes(room.name)) {
              existing.rooms.push(room.name);
            }
          }

          map.set(portalProductId, existing);
        }
      }
    });

    // Track fixtures placement
    fixtures.forEach(fixture => {
      // Check if this is a Portal fixture by looking at category_info
      if (fixture.category_info?.from_portal && fixture.category_info?.portal_product_id) {
        const portalProductId = fixture.category_info.portal_product_id;

        // Handle split fixtures differently
        if (
          fixture.category_info.is_split &&
          fixture.category_info.split_parent_id &&
          fixture.category_info.split_suffix
        ) {
          // Create a unique key for split items: parent_id + suffix
          const splitKey = `${fixture.category_info.split_parent_id}_split_${fixture.category_info.split_suffix}`;
          const existing = map.get(splitKey) || { count: 0, rooms: [] };
          existing.count = 1; // Split items are unique, count is always 1 when placed

          // Track room placement
          if (fixture.room_id && floorPlanData?.rooms) {
            const room = floorPlanData.rooms.find(r => r.id === fixture.room_id);
            if (room && room.name && !existing.rooms.includes(room.name)) {
              existing.rooms.push(room.name);
            }
          }

          map.set(splitKey, existing);
        } else {
          // Regular Portal fixture (non-split)
          const existing = map.get(portalProductId) || { count: 0, rooms: [] };
          existing.count += 1;

          // Track room placement
          if (fixture.room_id && floorPlanData?.rooms) {
            const room = floorPlanData.rooms.find(r => r.id === fixture.room_id);
            if (room && room.name && !existing.rooms.includes(room.name)) {
              existing.rooms.push(room.name);
            }
          }

          map.set(portalProductId, existing);
        }
      }
    });

    return map;
  }, [lvProducts, controls, fixtures, floorPlanData?.rooms]);

  // Helper function to check if a product instance is placed
  const isProductPlaced = (product: any): boolean => {
    // Check if this is a grouped product (split parent with children)
    if (product.is_group && product.group_children) {
      // A grouped product is considered placed if ALL children are placed
      return product.group_children.every((child: any) => isProductPlaced(child));
    }

    // Check if this is a split product
    if (product.is_split && product.split_parent_id && product.split_suffix) {
      // For split products, check using the unique split key (parent_id + suffix)
      const splitKey = `${product.split_parent_id}_split_${product.split_suffix}`;
      const placementInfo = placedProductsMap.get(splitKey);
      return placementInfo ? placementInfo.count > 0 : false;
    }

    // For regular products or quantity-expanded products
    const originalId =
      product.id.includes('_') && !product.id.includes('_split_')
        ? product.id.split('_')[0]
        : product.id;
    const originalProduct = products.find(p => p.id === originalId);

    if (!originalProduct) return false;

    const placementInfo = placedProductsMap.get(originalProduct.portal_product_id);
    if (!placementInfo) return false;

    // For quantity-expanded products (with _index suffix), check if this specific instance is placed
    if (product.id.includes('_') && !product.id.includes('_split_')) {
      const parts = product.id.split('_');
      const instanceIndex = parseInt(parts[parts.length - 1], 10);
      return !isNaN(instanceIndex) && instanceIndex < placementInfo.count;
    }

    return placementInfo.count > 0;
  };

  // Get placement info for a product
  const getPlacementInfo = (product: any) => {
    // Check if this is a split product
    if (product.is_split && product.split_parent_id && product.split_suffix) {
      // For split products, check using the unique split key (parent_id + suffix)
      const splitKey = `${product.split_parent_id}_split_${product.split_suffix}`;
      return placedProductsMap.get(splitKey) || null;
    }

    // For regular products
    const originalId =
      product.id.includes('_') && !product.id.includes('_split_')
        ? product.id.split('_')[0]
        : product.id;
    const originalProduct = products.find(p => p.id === originalId);

    if (!originalProduct) return null;

    return placedProductsMap.get(originalProduct.portal_product_id);
  };

  const loadProducts = async () => {
    if (!organization?.id || !floorPlanData?.floorPlan?.id) return;

    try {
      setLoading(true);

      // Load imported products with their product_library details for subcategory
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('portal_imported_products')
        .select(
          `
          *,
          product_library!portal_imported_products_product_library_id_fkey (
            subcategory
          )
        `,
        )
        .eq('clerk_org_id', organization.id)
        .eq('floor_plan_id', floorPlanData.floorPlan.id)
        .order('imported_at', { ascending: false });

      if (error) throw error;

      // Load split configurations
      const splitService = createSplitConfigService(organization.id);
      const configs = await splitService.getAllSplitConfigs();
      setSplitConfigs(configs);

      setProducts(data || []);

      // Get the first proposal info (we work with one proposal per floor plan)
      if (data && data.length > 0 && data[0].proposal_name) {
        setCurrentProposal({
          id: data[0].proposal_id,
          name: data[0].proposal_name,
          number: data[0].proposal_id,
        });
      }

      // Auto-expand area - prefer last used area from localStorage, fall back to first area
      if (data && data.length > 0) {
        const storageKey = `portal-drawer-expanded-area-${floorPlanData.floorPlan.id}`;
        const savedArea = localStorage.getItem(storageKey);

        // Get all unique areas from the data
        const availableAreas = [
          ...new Set(data.map(p => p.portal_hierarchy?.area).filter(Boolean)),
        ];

        // Check if saved area exists in current data
        const areaToExpand =
          savedArea && availableAreas.includes(savedArea)
            ? savedArea
            : data[0].portal_hierarchy?.area;

        if (areaToExpand) {
          setExpandedAreas(new Set([areaToExpand]));

          // Find the first option in the selected area
          const areaProducts = data.filter(p => p.portal_hierarchy?.area === areaToExpand);
          const firstOption = areaProducts[0]?.portal_hierarchy?.option;
          if (firstOption) {
            setExpandedOptions(new Set([`${areaToExpand}-${firstOption}`]));
          }
        }
      }
    } catch (error) {
      logger.error('Error loading products:', error);
      toast.error('Failed to load imported products');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadProducts();
    toast.success('Products refreshed');
  };

  const handleOpenSplitModal = (product: ImportedProduct) => {
    setProductToSplit(product);
    setShowSplitModal(true);
  };

  const handleOpenProductDetails = (product: ImportedProduct) => {
    if (!floorPlanData?.floorPlan?.id) return;

    // Find the entity that corresponds to this portal product
    // Check all three entity types: lvProducts, controls, and fixtures
    const lvProducts = floorPlanData.lvProducts || [];
    const controls = floorPlanData.controls || [];
    const fixtures = floorPlanData.fixtures || [];

    // Check LV products (use metadata field)
    const lvProduct = lvProducts.find(
      p =>
        p.metadata?.portal_product_id === product.portal_product_id ||
        p.metadata?.reference_id === product.reference_id,
    );

    // Check controls (use category_info field)
    const control = controls.find(
      c =>
        c.category_info?.portal_product_id === product.portal_product_id ||
        c.category_info?.reference_id === product.reference_id,
    );

    // Check fixtures (use category_info field)
    const fixture = fixtures.find(
      f =>
        f.category_info?.portal_product_id === product.portal_product_id ||
        f.category_info?.reference_id === product.reference_id,
    );

    // Clear current selection
    clearEntitySelection();

    // Route to the appropriate drawer based on what was found
    if (lvProduct) {
      selectEntity('product', lvProduct.id);
      setActiveDrawer('product');
    } else if (control) {
      selectEntity('control', control.id);
      setActiveDrawer('control');
    } else if (fixture) {
      selectEntity('fixture', fixture.id);
      setActiveDrawer('fixture');
    } else {
      toast.info('Product must be placed on floor plan first to edit details');
    }
  };

  const handleSplitSave = async () => {
    // Reload products and split configs after saving
    await loadProducts();
    setShowSplitModal(false);
    setProductToSplit(null);

    // Trigger floor plan data refresh to update lvProducts
    // This will update the placement tracking
    if (floorPlanData?.refetch) {
      await floorPlanData.refetch();
    }
  };

  const handleRefreshProposal = async () => {
    if (!floorPlanData?.floorPlan?.id) return;

    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const placedIds: string[] = [];

      // Fetch placed LV products with portal_product_id
      const { data: lvProductsData } = await supabase
        .from('lv_products')
        .select('metadata')
        .eq('floor_plan_id', floorPlanData.floorPlan.id)
        .eq('metadata->from_portal', true);

      if (lvProductsData) {
        lvProductsData.forEach((product: any) => {
          const portalProductId = product.metadata?.portal_product_id;
          if (portalProductId) {
            // For split items, create a unique identifier using portal_product_id + split_suffix
            const isSplit = product.metadata?.is_split;
            const splitSuffix = product.metadata?.split_suffix;

            if (isSplit && splitSuffix) {
              // Use combination of product ID and split suffix for unique tracking
              placedIds.push(`${portalProductId}:${splitSuffix}`);
            } else {
              // Regular non-split item
              placedIds.push(portalProductId);
            }
          }
        });
      }

      // Fetch placed controls with portal_product_id
      const { data: controlsData, error: controlsError } = await supabase
        .from('lighting_controls')
        .select('category_info')
        .eq('floor_plan_id', floorPlanData.floorPlan.id)
        .eq('category_info->from_portal', true);

      if (controlsData) {
        controlsData.forEach((control: any) => {
          const portalProductId = control.category_info?.portal_product_id;
          if (portalProductId) {
            // For split items, create a unique identifier using portal_product_id + split_suffix
            const isSplit = control.category_info?.is_split || control.category_info?.is_split_item;
            const splitSuffix = control.category_info?.split_suffix;

            if (isSplit && splitSuffix) {
              // Use combination of product ID and split suffix for unique tracking
              placedIds.push(`${portalProductId}:${splitSuffix}`);
            } else {
              // Regular non-split item
              placedIds.push(portalProductId);
            }
          }
        });
      }

      // Set the placed product IDs and open the modal
      setPlacedProductIds(placedIds);
      setShowImportModal(true);
    } catch (error) {
      logger.error('Error fetching placed products:', error);
      toast.error('Failed to refresh proposal');
    }
  };

  const handleProductsImported = () => {
    // Reload products after successful import
    loadProducts();
    setShowImportModal(false);
    toast.success('Products updated successfully');
  };

  const handleUnlinkProposal = async () => {
    if (!organization?.id || !currentProposal) return;

    try {
      setIsUnlinking(true);

      const result = await portalUnlinkService.unlinkProposal(
        organization.id,
        floorPlanData?.floorPlan?.id,
        currentProposal.id,
      );

      if (result.success) {
        // Clear local state
        setProducts([]);
        setCurrentProposal(null);
        setExpandedAreas(new Set());
        setExpandedOptions(new Set());

        // Close the drawer
        handleClose();

        // Refresh floor plan data to update lv_products
        if (floorPlanData?.refetch) {
          await floorPlanData.refetch();
        }

        toast.success(
          `Proposal unlinked. ${result.productsConverted} products converted to generic items.`,
        );

        // Dispatch event to notify other components (like PortalioButton)
        window.dispatchEvent(new CustomEvent('portal-proposal-unlinked'));

        // Force a small delay to ensure database changes are committed
        setTimeout(() => {
          // Reload the imported products count in the button
          // This will cause the button to show import modal instead of drawer
          loadProducts();
        }, 500);
      } else {
        toast.error(result.error || 'Failed to unlink proposal');
      }
    } catch (error) {
      logger.error('Error unlinking proposal:', error);
      toast.error('Failed to unlink proposal');
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleClose = () => {
    setActiveDrawer(null);
  };

  const handleDragStart = (e: React.DragEvent, product: any) => {
    // Handle dragging grouped products or individual items
    const originalId = product.id.includes('_') ? product.id.split('_')[0] : product.id;

    // Check if this is a group being dragged
    if (product.is_group && product.group_children) {
      // Dragging a parent group - include only unplaced children
      const unplacedChildren = product.group_children.filter(
        (child: any) => !isProductPlaced(child),
      );

      // If no unplaced children, don't allow drag
      if (unplacedChildren.length === 0) {
        e.preventDefault();
        return;
      }

      const dragData = {
        type: 'portal-product-group',
        products: unplacedChildren.map((child: any) => ({
          id: child.product_library_id || child.id,
          name: child.portal_name,
          description: child.portal_description,
          price: child.portal_price,
          category: splitCategories.get(child.id) || child.category,
          // Use split's subcategory first, then fallback to product_library
          subcategory: child.subcategory || child.product_library?.subcategory,
          manufacturer: child.manufacturer,
          model: child.model,
          isPortalProduct: true,
          portalProductId: child.portal_product_id,
          productLibraryId: child.product_library_id,
          isSplit: true,
          splitSuffix: child.split_suffix,
          splitParentId: child.split_parent_id,
          splitParentReference: child.split_parent_reference,
        })),
        autoSpace: true, // Flag to auto-space items when dropped
      };

      // Set dragged item in app store
      useAppStore.getState().setDraggedItem(dragData);

      // Also set data transfer for visual feedback
      e.dataTransfer.setData('application/json', JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'copy';
    } else {
      // Dragging individual item (could be a split child or regular product)
      const dragData = {
        type: 'portal-product',
        product: {
          id: product.product_library_id || originalId,
          name: product.portal_name,
          description: product.portal_description,
          price: product.portal_price,
          category: product.is_split
            ? splitCategories.get(product.id) || product.category
            : product.category,
          // For split items, use split's subcategory; otherwise use product_library subcategory
          subcategory: product.is_split
            ? product.subcategory || product.product_library?.subcategory
            : product.product_library?.subcategory,
          manufacturer: product.manufacturer,
          model: product.model,
          isPortalProduct: true,
          portalProductId: product.portal_product_id,
          productLibraryId: product.product_library_id,
          // Add split metadata if this is a split item
          isSplit: product.is_split || false,
          splitSuffix: product.split_suffix,
          splitParentId: product.split_parent_id,
          splitParentReference: product.split_parent_reference,
        },
      };

      // Set dragged item in app store
      useAppStore.getState().setDraggedItem(dragData);

      // Also set data transfer for visual feedback
      e.dataTransfer.setData('application/json', JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'copy';
    }
  };

  const handleDragEnd = () => {
    // Clear dragged item when drag ends (in case drop didn't happen)
    useAppStore.getState().setDraggedItem(null);
  };

  const toggleArea = (area: string) => {
    const newExpanded = new Set(expandedAreas);
    if (newExpanded.has(area)) {
      newExpanded.delete(area);
    } else {
      newExpanded.add(area);
    }
    setExpandedAreas(newExpanded);

    // Persist the last expanded area to localStorage
    if (floorPlanData?.floorPlan?.id) {
      const storageKey = `portal-drawer-expanded-area-${floorPlanData.floorPlan.id}`;
      if (newExpanded.size > 0) {
        // Save the most recently expanded area (last one added)
        const areasArray = Array.from(newExpanded);
        localStorage.setItem(storageKey, areasArray[areasArray.length - 1]);
      } else {
        localStorage.removeItem(storageKey);
      }
    }
  };

  const toggleOption = (area: string, option: string) => {
    const key = `${area}-${option}`;
    const newExpanded = new Set(expandedOptions);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedOptions(newExpanded);
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleSplitCategoryChange = (splitId: string, category: string) => {
    const newCategories = new Map(splitCategories);
    newCategories.set(splitId, category);
    setSplitCategories(newCategories);
  };

  // Helper function to get the category template for a product
  const getCategoryTemplate = (category?: string, subcategory?: string) => {
    if (!category) return undefined;

    // First try to find exact match with subcategory
    if (subcategory) {
      const exactMatch = categoryTemplates.find(
        t => t.category === category && t.subcategory === subcategory,
      );
      if (exactMatch) return exactMatch;
    }

    // Fall back to parent category template
    return categoryTemplates.find(t => t.category === category && !t.subcategory);
  };

  // Component to render a product item (handles both regular and grouped products)
  const ProductItem = ({ product }: { product: any }) => {
    const isPlaced = isProductPlaced(product);
    const placementInfo = getPlacementInfo(product);
    const isGroup = product.is_group;
    const isExpanded = expandedGroups.has(product.id);

    if (isGroup && product.group_children) {
      // Render grouped product with expandable children
      const allPlaced = product.group_children.every((child: any) => isProductPlaced(child));
      const somePlaced = product.group_children.some((child: any) => isProductPlaced(child));

      return (
        <div className="space-y-1">
          {/* Parent Group */}
          <div
            draggable={!allPlaced}
            onDragStart={e => !allPlaced && handleDragStart(e, product)}
            onDragEnd={handleDragEnd}
            style={{
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
            className={`bg-card border rounded-lg p-2 transition-all ${
              allPlaced
                ? 'border-green-500/50 bg-green-50/5 cursor-default'
                : somePlaced
                  ? 'border-yellow-500/50 bg-yellow-50/5 hover:shadow-md'
                  : 'hover:shadow-md'
            }`}
          >
            <div className="flex items-center gap-2">
              {/* Drag Handle - Visual Indicator */}
              {!allPlaced && (
                <div
                  className="flex-shrink-0 p-1 -ml-1 text-muted-foreground pointer-events-none"
                  style={{
                    WebkitTouchCallout: 'none',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                  }}
                  title="Drag to floor plan"
                >
                  <GripVertical className="h-4 w-4" />
                </div>
              )}
              <div className="flex items-center justify-between gap-2 flex-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 p-0"
                  onClick={() => toggleGroup(product.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </Button>
                <div className="text-sm flex items-center gap-2">
                  <Split
                    className="h-3.5 w-3.5 text-muted-foreground"
                    title={`${product.group_children.length} split items`}
                  />
                  <span className="font-medium">
                    {product.manufacturer && product.model
                      ? `${product.manufacturer} • ${product.model}`
                      : product.portal_name}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenProductDetails(product);
                  }}
                  title="Product details"
                >
                  <Settings2 className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenSplitModal(product);
                  }}
                  title="Configure split"
                >
                  <Split className="h-3 w-3" />
                </Button>
                {allPlaced && (
                  <Badge variant="secondary" className="h-5 px-1 gap-1">
                    <Check className="h-3 w-3" />
                    <span className="text-xs">All Placed</span>
                  </Badge>
                )}
                {somePlaced && !allPlaced && (
                  <Badge
                    variant="outline"
                    className="h-5 px-1 gap-1 text-yellow-600 border-yellow-500/50"
                  >
                    <span className="text-xs">
                      {product.group_children.filter((c: any) => isProductPlaced(c)).length}/
                      {product.group_children.length} Placed
                    </span>
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Children (Split Items) */}
          {isExpanded && (
            <div className="pl-8 space-y-1">
              {product.group_children.map((child: any) => {
                const childIsPlaced = isProductPlaced(child);
                const childPlacementInfo = getPlacementInfo(child);
                const childCategory = splitCategories.get(child.id) || child.category || 'other';
                const childTemplate = getCategoryTemplate(childCategory, child.subcategory);

                return (
                  <div
                    key={child.id}
                    draggable={!childIsPlaced}
                    onDragStart={e => !childIsPlaced && handleDragStart(e, child)}
                    onDragEnd={handleDragEnd}
                    style={{
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                    }}
                    className={`bg-card/50 border rounded-md p-2 transition-all ${
                      childIsPlaced
                        ? 'border-green-500/30 bg-green-50/3 cursor-default'
                        : 'hover:shadow-sm hover:bg-card'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {/* Drag Handle - Visual Indicator */}
                      {!childIsPlaced && (
                        <div
                          className="flex-shrink-0 p-1 -ml-1 text-muted-foreground pointer-events-none"
                          style={{
                            WebkitTouchCallout: 'none',
                            WebkitUserSelect: 'none',
                            userSelect: 'none',
                          }}
                          title="Drag to floor plan"
                        >
                          <GripVertical className="h-4 w-4" />
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 flex-1">
                        <div className="text-sm flex items-center gap-2">
                          <span>{child.portal_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {childIsPlaced && (
                            <div className="flex items-center gap-1">
                              {childPlacementInfo?.rooms.length > 0 && (
                                <span
                                  className="text-xs text-muted-foreground"
                                  title={`Placed in: ${childPlacementInfo.rooms.join(', ')}`}
                                >
                                  <MapPin className="h-3 w-3" />
                                </span>
                              )}
                              <Badge variant="secondary" className="h-5 px-1 gap-1">
                                <Check className="h-3 w-3" />
                                <span className="text-xs">Placed</span>
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    } else {
      // Regular product (not grouped)
      return (
        <div
          draggable={!isPlaced}
          onDragStart={e => !isPlaced && handleDragStart(e, product)}
          onDragEnd={handleDragEnd}
          style={{
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
          className={`bg-card border rounded-lg p-2 transition-all ${
            isPlaced ? 'border-green-500/50 bg-green-50/5 cursor-default' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center gap-2">
            {/* Drag Handle - Visual Indicator */}
            {!isPlaced && (
              <div
                className="flex-shrink-0 p-1 -ml-1 text-muted-foreground pointer-events-none"
                style={{
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                }}
                title="Drag to floor plan"
              >
                <GripVertical className="h-4 w-4" />
              </div>
            )}
            <div className="flex items-center justify-between gap-2 flex-1">
              <div className="text-sm flex-1 flex items-center gap-2">
                {product.manufacturer && product.model ? (
                  <span>
                    {product.manufacturer} • {product.model}
                  </span>
                ) : (
                  <span>{product.portal_name}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenProductDetails(product);
                  }}
                  title="Product details"
                >
                  <Settings2 className="h-3 w-3" />
                </Button>
                {!isPlaced && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={e => {
                      e.stopPropagation();
                      handleOpenSplitModal(product);
                    }}
                    title="Configure split"
                  >
                    <Split className="h-3 w-3" />
                  </Button>
                )}
                {isPlaced && (
                  <>
                    {placementInfo?.rooms.length > 0 && (
                      <span
                        className="text-xs text-muted-foreground"
                        title={`Placed in: ${placementInfo.rooms.join(', ')}`}
                      >
                        <MapPin className="h-3 w-3" />
                      </span>
                    )}
                    <Badge variant="secondary" className="h-5 px-1 gap-1">
                      <Check className="h-3 w-3" />
                      <span className="text-xs">Placed</span>
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch =
      searchQuery === '' ||
      product.portal_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.model?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.portal_hierarchy?.area?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.portal_hierarchy?.option?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  // Expand products by quantity and apply splits
  // For grouped display: create parent-child structure
  interface GroupedProduct extends ImportedProduct {
    is_group?: boolean;
    group_children?: ImportedProduct[];
    group_parent_id?: string;
  }

  const expandedProducts: GroupedProduct[] = [];

  filteredProducts.forEach(product => {
    // Check if product has a split configuration
    const splitConfig =
      splitConfigs.get(product.reference_id || '') ||
      splitConfigs.get(`${product.manufacturer}:::${product.model}`);

    const quantity = product.portal_data?.quantity || 1;

    if (splitConfig && splitConfig.length > 0) {
      // Product has splits - create grouped structure
      // For each quantity unit, create a parent with split children
      for (let q = 0; q < quantity; q++) {
        const parentId = quantity > 1 ? `${product.id}_q_${q}` : product.id;
        const parentName =
          quantity > 1 ? `${product.portal_name} (${q + 1}/${quantity})` : product.portal_name;

        // Create split children for this parent
        const children: ImportedProduct[] = splitConfig.map(
          (split, index) =>
            ({
              ...product,
              id: `${parentId}_split_${split.suffix}`,
              // Always include suffix, then use custom name if provided, otherwise use model
              portal_name: `${split.suffix} - ${split.name || product.model || product.portal_name}`,
              category: split.category || product.category,
              subcategory: split.subcategory || product.subcategory,
              quantity: split.quantity || 1,
              is_split: true,
              split_suffix: split.suffix,
              split_parent_id: parentId,
              split_parent_reference: product.reference_id,
              split_index: index,
              group_parent_id: parentId,
            }) as any,
        );

        // Create parent product with children
        expandedProducts.push({
          ...product,
          id: parentId,
          portal_name: parentName,
          is_group: true,
          group_children: children,
          // Parent shows total splits as quantity
          quantity: splitConfig.length,
        } as GroupedProduct);
      }
    } else {
      // No splits - regular quantity expansion
      for (let i = 0; i < quantity; i++) {
        expandedProducts.push({
          ...product,
          id: quantity > 1 ? `${product.id}_${i}` : product.id,
          portal_name:
            quantity > 1 ? `${product.portal_name} (${i + 1}/${quantity})` : product.portal_name,
        });
      }
    }
  });

  // Organize products hierarchically
  const hierarchicalProducts: HierarchicalStructure = expandedProducts.reduce((acc, product) => {
    const area = product.portal_hierarchy?.area || 'Other';
    const option = product.portal_hierarchy?.option || 'General';

    if (!acc[area]) {
      acc[area] = {};
    }
    if (!acc[area][option]) {
      acc[area][option] = [];
    }
    acc[area][option].push(product);

    return acc;
  }, {} as HierarchicalStructure);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={open => !open && handleClose()} modal={false}>
        <SideSheetContent
          side="right"
          className="w-[400px] sm:w-[540px] flex flex-col"
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
        >
          <SheetHeader className="space-y-3">
            <SheetTitle className="sr-only">Portal.io Products Drawer</SheetTitle>
            <div className="flex items-center justify-between">
              <img src="/portal-logo.png" alt="Portal.io" className="h-6" />
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefreshProposal}
                  disabled={loading || isUnlinking}
                  className="h-8"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  <span className="text-xs">Refresh</span>
                </Button>

                {currentProposal && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" disabled={isUnlinking} className="h-8">
                        {isUnlinking ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <X className="h-3 w-3 mr-1" />
                        )}
                        <span className="text-xs">Unlink</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Unlink Portal.io Proposal?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will unlink "{currentProposal.name}" from this floor plan.
                          <br />
                          <br />
                          • Products already placed will remain on the floor plan as generic items
                          <br />
                          • You can import a new proposal at any time
                          <br />• This action cannot be undone
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleUnlinkProposal}>
                          Unlink Proposal
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
            {currentProposal && (
              <div className="space-y-1">
                <h3 className="font-medium text-sm">{currentProposal.name}</h3>
                {currentProposal.number && (
                  <p className="text-xs text-muted-foreground">
                    Proposal #{currentProposal.number}
                  </p>
                )}
              </div>
            )}
            <SheetDescription className="text-xs">
              Drag products to your floor plan
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products, areas, or options..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                onKeyUp={e => e.stopPropagation()}
                className="pl-10"
              />
            </div>
          </div>

          <ScrollArea className="flex-1 mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : expandedProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  {searchQuery ? 'No products found' : 'No products imported yet'}
                </p>
                {!searchQuery && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Click the Portal.io button to import products
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2 pb-4">
                {Object.entries(hierarchicalProducts).map(([area, options]) => {
                  // Check if area has only one option
                  const optionKeys = Object.keys(options);
                  const hasSingleOption = optionKeys.length === 1;
                  const singleOptionKey = hasSingleOption ? optionKeys[0] : null;

                  return (
                    <Collapsible
                      key={area}
                      open={expandedAreas.has(area)}
                      onOpenChange={() => toggleArea(area)}
                    >
                      <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 hover:bg-accent rounded-md transition-colors">
                        {expandedAreas.has(area) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        <span className="font-medium flex-1 text-left">{area}</span>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const areaProducts = Object.values(options).flat();
                            const placedCount = areaProducts.filter(p => isProductPlaced(p)).length;
                            return placedCount > 0 ? (
                              <>
                                <Badge
                                  variant="outline"
                                  className="text-xs text-green-600 border-green-500/50"
                                >
                                  {placedCount} placed
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {areaProducts.length} total
                                </Badge>
                              </>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                {areaProducts.length}
                              </Badge>
                            );
                          })()}
                        </div>
                      </CollapsibleTrigger>

                      <CollapsibleContent className="pl-6 space-y-2 mt-2">
                        {hasSingleOption && singleOptionKey ? (
                          // If only one option, show products directly under area
                          <div className="space-y-2">
                            {options[singleOptionKey].map(product => (
                              <ProductItem key={product.id} product={product} />
                            ))}
                          </div>
                        ) : (
                          // If multiple options, show the hierarchy
                          Object.entries(options).map(([option, items]) => (
                            <Collapsible
                              key={`${area}-${option}`}
                              open={expandedOptions.has(`${area}-${option}`)}
                              onOpenChange={() => toggleOption(area, option)}
                            >
                              <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 hover:bg-accent rounded-md transition-colors">
                                {expandedOptions.has(`${area}-${option}`) ? (
                                  <ChevronDown className="h-3 w-3" />
                                ) : (
                                  <ChevronRight className="h-3 w-3" />
                                )}
                                <Box className="h-3 w-3 text-muted-foreground" />
                                <span className="text-sm flex-1 text-left">{option}</span>
                                <Badge variant="outline" className="text-xs">
                                  {items.length}
                                </Badge>
                              </CollapsibleTrigger>

                              <CollapsibleContent className="pl-6 space-y-2 mt-2">
                                {items.map(product => (
                                  <ProductItem key={product.id} product={product} />
                                ))}
                              </CollapsibleContent>
                            </Collapsible>
                          ))
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </SideSheetContent>
      </Sheet>

      {/* Import Modal for updates */}
      <PortalImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onProductsImported={handleProductsImported}
        preselectedProposal={
          currentProposal
            ? {
                id: parseInt(currentProposal.id, 10),
                proposalName: currentProposal.name,
                proposalNumber: parseInt(currentProposal.number || currentProposal.id, 10),
                customerName: '',
                status: 'active',
                totalAmount: 0,
                createdDate: new Date().toISOString(),
              }
            : undefined
        }
        placedProductIds={placedProductIds}
        skipToProducts={placedProductIds.length > 0}
      />

      {/* Split Configuration Modal */}
      {productToSplit && (
        <SplitConfigModal
          isOpen={showSplitModal}
          onClose={() => {
            setShowSplitModal(false);
            setProductToSplit(null);
          }}
          product={{
            id: productToSplit.portal_product_id,
            reference_id: productToSplit.reference_id || productToSplit.portal_product_id,
            portal_product_id: productToSplit.portal_product_id,
            portal_name: productToSplit.portal_name,
            brand: productToSplit.manufacturer || undefined,
            model: productToSplit.model || undefined,
            category: productToSplit.category,
            quantity: productToSplit.portal_data?.quantity,
          }}
          existingConfig={splitConfigs.get(productToSplit.reference_id || '') || undefined}
          onSave={handleSplitSave}
          floorPlanId={floorPlanData?.floorPlan?.id}
        />
      )}
    </>
  );
}
